import { useEffect, useState } from "react";
import yaml from "js-yaml";
import { API_SPECS } from "./constants/apiSpecs";
import MainContent from "./components/MainContent";
import Sidebar from "./components/Sidebar";

const getInitialApi = () => {
  const apiIdFromHash = window.location.hash.substring(1);
  return API_SPECS.find((s) => s.id === apiIdFromHash) || API_SPECS[0];
};

export default function App() {
  const [apiSpec, setApiSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [selectedApi, setSelectedApi] = useState(() => getInitialApi());

  useEffect(() => {
    const onHashChange = () => {
      const apiId = window.location.hash.substring(1);
      const apiConfig = API_SPECS.find((s) => s.id === apiId);
      if (apiConfig) {
        setSelectedApi(apiConfig);
        setActiveSection("overview");
      }
    };

    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchApiSpec = async () => {
      setLoading(true);
      setError(null);
      setActiveSection("overview");

      try {
        const response = await fetch(selectedApi.url);
        if (!response.ok) {
          throw new Error(`Failed to fetch API specification (${response.status})`);
        }
        const yamlText = await response.text();
        const parsed = yaml.load(yamlText);
        if (!cancelled) {
          setApiSpec(parsed);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchApiSpec();
    return () => {
      cancelled = true;
    };
  }, [selectedApi]);

  const handleApiChange = (apiConfig) => {
    window.location.hash = `#${apiConfig.id}`;
    setSelectedApi(apiConfig);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading {selectedApi.name}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="loading">
        <h2>Error Loading Documentation</h2>
        <p>{error}</p>
        <p style={{ fontSize: "14px", marginTop: "12px" }}>
          Make sure the YAML file is accessible at: <code>{selectedApi.url}</code>
        </p>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar
        apiSpec={apiSpec}
        selectedApi={selectedApi}
        onApiChange={handleApiChange}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />
      <MainContent apiSpec={apiSpec} activeSection={activeSection} />
    </div>
  );
}
